import { Box, Button, Grid2, useMediaQuery } from '@mui/material';
import { useUserSearchContext } from 'context/UserSearchContext';
import { format } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const days: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const monthNames: string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface CalendarProps {
  year: number;
  currentYear: number;
}

export const Calendar: React.FC<CalendarProps> = ({ currentYear, year }) => {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const { bookingSearchData, setBookingSearchData } = useUserSearchContext();

  const { dateTypeParams } = useParams();
  const navigate = useNavigate();
  const selectedDateInit =
    dateTypeParams === 'pickup-date'
      ? new Date(bookingSearchData.OutDetails.Date)
      : new Date(bookingSearchData.InDetails.Date);

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    selectedDateInit
  );
  const objectKeyDate =
    dateTypeParams === 'pickup-date' ? 'pickupDate' : 'returnDate';

  const today = new Date();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();

  const targetRef = useRef<HTMLDivElement | null>(null);
  const scrolToTop = useRef<HTMLDivElement | null>(null);

  const generateDays = (month: number): (number | null)[] => {
    const date = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dayOfWeek = date.getDay();

    const daysArray: (number | null)[] = Array(dayOfWeek).fill(null);
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }
    return daysArray;
  };

  const handleDayClick = (day: number, month: number): void => {
    const date = new Date(year, month, day);
    const returnDate = new Date(date);
    setSelectedDate(date);

    if (objectKeyDate === 'pickupDate') {
      // Auto set return date +1 day
      returnDate.setDate(date.getDate() + 1);

      setBookingSearchData((prev) => ({
        ...prev,
        OutDetails: {
          ...prev.OutDetails, // ✅ use prev, not bookingSearchData
          Date: format(date, 'dd MMM yyyy'),
        },
        InDetails: {
          ...prev.InDetails, // ✅ use prev
          Date: format(returnDate, 'dd MMM yyyy'),
        },
        IPLocation: { ...prev.IPLocation },
        insuranceChargeCode: [...prev.insuranceChargeCode],
        ancilarriesChargeCode: [...prev.ancilarriesChargeCode],
        chargeCodes: [...prev.chargeCodes],
        activeBranchId: '',
        branchType: '',
      }));
    } else {
      setBookingSearchData((prev) => ({
        ...prev,
        InDetails: {
          ...prev.InDetails, // ✅ use prev
          Date: format(returnDate, 'dd MMM yyyy'),
        },
        IPLocation: { ...prev.IPLocation },
        insuranceChargeCode: [...prev.insuranceChargeCode],
        ancilarriesChargeCode: [...prev.ancilarriesChargeCode],
        chargeCodes: [...prev.chargeCodes],
        activeBranchId: '',
        branchType: '',
      }));
    }

    navigate(-1);
  };

  return (
    <Grid2 container spacing={2} width="90%" margin="auto" ref={scrolToTop}>
      {monthNames.map((month, index) => {
        // Skip rendering previous months of the current year
        if (year === currentYear && index < currentMonth) return null;

        return (
          <Grid2
            size={{ xs: isPortrait ? 12 : 6 }}
            className="month"
            key={index}
            sx={{ borderRadius: '20px' }}
            ref={index === currentMonth ? targetRef : null}
          >
            <h3>
              {month} {year}
            </h3>
            <div className="days-header">
              {days.map((day, idx) => (
                <div key={idx} className="day-name">
                  {day}
                </div>
              ))}
            </div>

            <div className="days-grid">
              {generateDays(index).map((day, idx) => {
                const isSelected =
                  selectedDate &&
                  selectedDate.getDate() === day &&
                  selectedDate.getMonth() === index &&
                  selectedDate.getFullYear() === year;

                const isDisabled =
                  day !== null &&
                  year === currentYear &&
                  index === currentMonth &&
                  day < currentDay;

                return (
                  <div
                    key={idx}
                    className={`day ${isSelected ? 'selected' : ''} ${isDisabled ? 'disablePrevMonths' : ''}`}
                    onClick={() =>
                      day != null && !isDisabled && handleDayClick(day, index)
                    }
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </Grid2>
        );
      })}
    </Grid2>
  );
};
